import { useCallback, useState, useRef } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { Check, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { type ProjectFormData, useUpdateProject, useGetProject } from '@zendia/frontend-shared'
import { axiosRequest } from '../../../../utilities/axiosClient'
import { ProjectForm, type ProjectFormRef } from '../../../../components/Projects/ProjectForm'

export const Route = createFileRoute('/_authenticated/projects/$projectId/edit')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
})

function RouteComponent() {
  const router = useRouter()
  const { projectId } = Route.useParams()
  const { data: projectResponse, isLoading, isError } = useGetProject(axiosRequest, projectId)
  const { mutateAsync: updateProject } = useUpdateProject(axiosRequest)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const projectFormRef = useRef<ProjectFormRef>(null)

  const onBack = useCallback(() => {
    router.navigate({ to: `/projects/${projectId}`, replace: true })
  }, [router, projectId])

  const onSave = useCallback(
    async (data: ProjectFormData) => {
      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        await updateProject({ projectId, data })
        onBack()
      } catch (error) {
        console.error(error)
        setSubmitError('Failed to update project')
      } finally {
        setIsSubmitting(false)
      }
    },
    [updateProject, projectId, onBack],
  )

  if (isLoading) {
    return <div>Loading collection details...</div>
  }

  if (isError || !projectResponse?.data) {
    return <div>Error loading collection details</div>
  }

  return (
    <FullView
      leftAction={
        <ActionButton
          onClick={onBack}
          label="Discard"
          icon={<ChevronBackward className="size-6" />}
        />
      }
      centerContent={<div>Edit Collection</div>}
      rightAction={
        <ActionButton
          primary
          onClick={() => {
            projectFormRef.current?.submit()
          }}
          label="Save changes"
          disabled={isSubmitting}
          icon={<Check className="m-auto size-6" />}
        />
      }
    >
      <FullViewContent>
        <div className="h-full">
          <ProjectForm
            ref={projectFormRef}
            initialData={projectResponse.data}
            onSubmit={onSave}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        </div>
      </FullViewContent>
    </FullView>
  )
}
